/// <amd-module name="TFS/Views/ProjectOverviewPage/ProjectOverviewTab/Actions/AboutProjectActionCreator" />
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";

import { Constants, TelemetryConstants } from "TFS/Views/ProjectOverviewPage/Constants";
import { ActionsHub } from "TFS/Views/ProjectOverviewPage/ProjectOverviewTab/Actions/ActionsHub";
import { OverviewTabTelemetryConstants, TfvcPrefix, WikiHomePage } from "TFS/Views/ProjectOverviewPage/ProjectOverviewTab/Constants";
import { AboutProjectData, SourceRepositoryTypes } from "TFS/Views/ProjectOverviewPage/ProjectOverviewTab/Models";
import { AboutProjectSource } from "TFS/Views/ProjectOverviewPage/ProjectOverviewTab/Sources/AboutProjectSource";
import { StoresHub } from "TFS/Views/ProjectOverviewPage/ProjectOverviewTab/Stores/StoresHub";
import { ProjectBasicData } from "TFS/Views/ProjectOverviewPage/Shared/Models";

export class AboutProjectActionCreator {
    constructor(
        private _pageContext: IVssPageContext,
        private _actionsHub: ActionsHub,
        private _storeHub: StoresHub,
        private _aboutProjectSource?: AboutProjectSource
    ) {}

    public loadInitialData = (): void => {
        this.loadAboutProjectData();
    };

    public loadAboutProjectData = (): void => {
        this.aboutProjectSource.getAboutProjectDataAsync().then(
            (pageData: AboutProjectData | undefined) => {
                if (pageData) {
                    this._actionsHub.aboutProjectDataLoaded.invoke(pageData);
                } else {
                    this._actionsHub.aboutProjectDataLoadFailed.invoke(void 0);
                }
            },
            error => {
                this._actionsHub.aboutProjectDataLoadFailed.invoke(void 0);
            }
        );
    };

    public loadAllProjectTagsInCollection = (): void => {
        const allProjectTagsInCollectionFetched = this._storeHub.aboutProjectStore.getState().allProjectTagsInCollectionData.tagsFetched;
        const { projectBasicInfo } = this._storeHub.projectBasicInfoStore.getState();

        if (!allProjectTagsInCollectionFetched) {
            this.aboutProjectSource.getAllProjectTagsInCollectionDataAsync().then(
                (tags: string[]) => {
                    this._logTelemetry(OverviewTabTelemetryConstants.ProjectTags_FetchAllProjectTags, projectBasicInfo, TelemetryConstants.Succeeded);
                    this._actionsHub.allProjectTagsInCollectionLoaded.invoke(tags);
                },
                () => {
                    this._logTelemetry(OverviewTabTelemetryConstants.ProjectTags_FetchAllProjectTags, projectBasicInfo, TelemetryConstants.Failed);
                    this._actionsHub.allProjectTagsInCollectionLoaded.invoke([]);
                }
            );
        }
    };

    public onSaveChanges = (isDescriptionChanged: boolean, isReadmeInfoChanged: boolean, isTagListChanged: boolean): void => {
        if (!isDescriptionChanged) {
            this._actionsHub.projectDescriptionNotUpdating.invoke(void 0);
        }
        if (!isReadmeInfoChanged) {
            this._actionsHub.readmeSettingNotUpdating.invoke(void 0);
        }
        if (!isTagListChanged) {
            this._actionsHub.projectTagsNotUpdating.invoke(void 0);
        }
    };

    public saveProjectDescription = (description: string): void => {
        this._actionsHub.projectDescriptionUpdateStarted.invoke(void 0);

        const { projectBasicInfo } = this._storeHub.projectBasicInfoStore.getState();

        this.aboutProjectSource.saveProjectDescription(description, projectBasicInfo!.id).then(
            () => {
                this._logTelemetry(OverviewTabTelemetryConstants.ProjectDescription_Update, projectBasicInfo, TelemetryConstants.Succeeded);
                // the project description update job succeeded, hence invoke project description updated action
                this._actionsHub.projectDescriptionUpdated.invoke(description);
            },
            error => {
                let message: string | undefined;
                if (error && error.serverError && error.serverError.typeKey === "ArgumentException") {
                    message = error.message;
                }
                this._logTelemetry(OverviewTabTelemetryConstants.ProjectDescription_Update, projectBasicInfo, TelemetryConstants.Failed);
                this._actionsHub.projectDescriptionUpdateFailed.invoke(message);
            }
        );
    };

    public saveReadmeSetting = (readmeSetting: string): void => {
        this._actionsHub.readmeSettingUpdateStarted.invoke(void 0);

        const { projectBasicInfo } = this._storeHub.projectBasicInfoStore.getState();
        const repoTypeAndId: string[] = readmeSetting ? readmeSetting.split(":") : [];
        // extracting repoId and type from readme setting
        let repoType: string | undefined;
        let repoId: string | undefined;
        if (repoTypeAndId.length === 2) {
            repoType = repoTypeAndId[0];
            repoId = repoTypeAndId[1];
        } else if (repoTypeAndId.length === 1) {
            repoId = repoTypeAndId[0];
        }

        let repositoryType: SourceRepositoryTypes;
        let repositoryId: string | undefined;

        if (repoId && repoId.startsWith(TfvcPrefix)) {
            repositoryType = SourceRepositoryTypes.Tfvc;
        } else {
            if (repoType && repoType === WikiHomePage) {
                repositoryType = SourceRepositoryTypes.Wiki;
            } else {
                repositoryType = SourceRepositoryTypes.Git;
            }
            repositoryId = repoId;
        }

        this.aboutProjectSource.saveReadmeSetting(readmeSetting, projectBasicInfo!.id).then(
            () => {
                this._logTelemetryForReadMeSettingUpdate(projectBasicInfo, TelemetryConstants.Succeeded, repositoryId, repositoryType);
                this._actionsHub.readmeSettingUpdated.invoke({ repositoryId, repositoryType });
            },
            () => {
                this._logTelemetryForReadMeSettingUpdate(projectBasicInfo, TelemetryConstants.Failed, repositoryId, repositoryType);
                this._actionsHub.readmeSettingUpdateFailed.invoke(void 0);
            }
        );
    };

    public saveProjectTags = (tags: string[]): void => {
        const initialTags = this._storeHub.aboutProjectStore.getState().aboutProjectData!.tagsData.tags;
        const addedTags = tags.filter(item => initialTags.indexOf(item) < 0);
        const removedTags = initialTags.filter(item => tags.indexOf(item) < 0);
        const addedTagsLength = addedTags.length;
        const removedTagsLength = removedTags.length;
        // If there are any addedTags or removedTags.
        if (addedTagsLength > 0 || removedTagsLength > 0) {
            this._actionsHub.projectTagsUpdateStarted.invoke(void 0);
            const { projectBasicInfo } = this._storeHub.projectBasicInfoStore.getState();
            const projectId = projectBasicInfo!.id;
            this.aboutProjectSource.saveProjectTag(addedTags, removedTags, projectId).then(
                () => {
                    this._logTelemetryForTagsUpdate(projectBasicInfo, TelemetryConstants.Succeeded, addedTagsLength, removedTagsLength);
                    this._actionsHub.projectTagsUpdated.invoke(tags);
                },
                () => {
                    this._logTelemetryForTagsUpdate(projectBasicInfo, TelemetryConstants.Failed, addedTagsLength, removedTagsLength);
                    this._actionsHub.projectTagsUpdateFailed.invoke(void 0);
                }
            );
        }
    };

    private get aboutProjectSource(): AboutProjectSource {
        if (!this._aboutProjectSource) {
            this._aboutProjectSource = new AboutProjectSource(this._pageContext);
        }

        return this._aboutProjectSource;
    }

    private _logTelemetryForTagsUpdate(
        projectBasicInfo: ProjectBasicData | undefined,
        status: string,
        addedTagsLength: number,
        removedTagsLength: number
    ) {
        this._logTelemetry(OverviewTabTelemetryConstants.ProjectTags_Update, projectBasicInfo, status, {
            AddedTagsCount: addedTagsLength,
            RemovedTagsCount: removedTagsLength
        });
    }

    private _logTelemetryForReadMeSettingUpdate(
        projectBasicInfo: ProjectBasicData | undefined,
        status: string,
        repositoryId: string | undefined,
        repositoryType: number
    ) {
        this._logTelemetry(OverviewTabTelemetryConstants.ProjectReadMeSettings_Update, projectBasicInfo, status, {
            [TelemetryConstants.RepositoryId]: repositoryId,
            [TelemetryConstants.RepositoryType]: SourceRepositoryTypes[repositoryType]
        });
    }

    private _logTelemetry(
        featureName: string,
        projectBasicInfo: ProjectBasicData | undefined,
        status: string,
        properties?: { [key: string]: any }
    ): void {
        properties = properties ? properties : {};
        properties[TelemetryConstants.ProjectId] = projectBasicInfo ? projectBasicInfo.id : undefined;
        properties[TelemetryConstants.Status] = status;
        const telemetryService = this._pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService.publishEvent(OverviewTabTelemetryConstants.Area, featureName, properties);
    }
}
